import AlertMessage from '@components/alerts/AlertMessage';
import Button from '@components/button/Button';
import { ContactUs } from '@components/contactUsLink/ContactUs';
import InputPhone, {
  CountryCodeMapping,
} from '@components/inputPhone/InputPhone';
import InputWithFormItem from '@components/inputWithFormItem/InputWithFormItem';
import { CenterPanel } from '@components/layout/CenterPanel/CenterPanel';
import { SurfaceContainer } from '@components/layout/surfaceContainer/SurfaceContainer';
import { BrandLogo } from '@components/logos/brandLogo/BrandLogo';
import { Form, Space, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MdBusiness,
  MdOutlineAccountCircle,
  MdOutlinePassword,
} from 'react-icons/md';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import {
  loginUser,
  logoutUser,
  useCreateTrialUser,
  useVerifySignupToken,
} from '../../actions';
import { trackSignup } from '../../helpers/analytics';
import { gaEvents, sendGASignUpEvent } from '../../helpers/gaTracker';
import {
  FeatureTypeKey,
  SIGNUP_TYPE_KEYS,
  SIGN_UP_FEATURE_PARAM_VALUES,
  SIGN_UP_PASSWORD_PATTERN,
  getRedirectPathFromFeatureType,
} from './helper';

const { Title, Text } = Typography;

interface CompleteSignUpProps {
  loginUserAction: (user: any) => void;
  logoutUserAction: () => void;
}

const CompleteSignUp: React.FC<CompleteSignUpProps> = ({
  loginUserAction,
  logoutUserAction,
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'loginAndSignup.completeSignUp',
  });

  useEffect(() => {
    sendGASignUpEvent(gaEvents.SIGNUP_COMPLETE_INFO, {});
  }, []);

  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState<any>({});

  const { signUpType, token, featureType } = useParams();
  const navigate = useNavigate();

  const {
    data,
    isLoading,
    error: errorResponse,
  } = useCreateTrialUser(userInfo, signUpType);

  const { error: tokenError } = useVerifySignupToken(token);

  const handleStartTrial = (e: any) => {
    const trialFeature =
      SIGN_UP_FEATURE_PARAM_VALUES[featureType as FeatureTypeKey];

    setUserInfo({
      ...e,
      token,
      trialFeature,
    });
  };

  const handleLogoutUser = async () => {
    await logoutUserAction();
    navigate(`/signup/${featureType}`);
  };

  useEffect(() => {
    if (data?.status === 'success') {
      const trialFeature =
        SIGN_UP_FEATURE_PARAM_VALUES[featureType as FeatureTypeKey];

      sendGASignUpEvent(gaEvents.SIGNUP_SUCCESS, {
        trialProduct: trialFeature,
      });

      if (signUpType === SIGNUP_TYPE_KEYS.default) {
        // Track signup for email/password registration
        trackSignup('email', trialFeature);

        const runLoginUser = async () => {
          await loginUserAction({
            email: data?.userEmail,
            password: userInfo?.password,
          });
        };

        runLoginUser().then(() =>
          navigate(getRedirectPathFromFeatureType(featureType)),
        );
      }

      if (signUpType === SIGNUP_TYPE_KEYS.sso) {
        // Note: trackSignup is already called in signupWithSSO for most cases.
        // However, if user comes through verify/sso route directly (edge case),
        // we should track it. But we can't determine provider from token reliably,
        // so we skip tracking here to avoid duplicates.
        // The main tracking happens in signupWithSSO function.

        window.location.href = getRedirectPathFromFeatureType(featureType);
      }
    }

    if (errorResponse) {
      const errorMessage = (errorResponse as any)?.response?.data?.error;
      setError(errorMessage);

      sendGASignUpEvent(gaEvents.SIGNUP_FAILED, {
        error: errorMessage,
      });
    }
  }, [
    data,
    errorResponse,
    featureType,
    loginUserAction,
    navigate,
    signUpType,
    userInfo?.password,
  ]);

  useEffect(() => {
    if (tokenError) {
      sendGASignUpEvent(gaEvents.SIGNUP_INVALID_TOKEN, {});
    }
  }, [tokenError]);

  const renderInvalidToken = () => (
    <Space
      orientation="vertical"
      size="small"
      className="tw-flex tw-justify-center tw-p-[16px]"
    >
      <BrandLogo size={56} wrapperClassName="tw-flex tw-justify-center" />
      <Text className="tw-flex tw-justify-center">{t('invalidToken')}</Text>
      <Button
        type="primary"
        onClick={handleLogoutUser}
        label={t('startOverBtn')}
        fullWidth
      />
    </Space>
  );

  const renderProfileForm = () => (
    <Space
      orientation="vertical"
      size="small"
      className="tw-w-full tw-p-[16px]"
    >
      <BrandLogo size={56} wrapperClassName="tw-flex tw-justify-center" />

      <Title level={2} className="!tw-m-0 tw-text-center">
        {t('header')}
      </Title>

      <Text className="tw-text-[--color-text-weaker] tw-flex tw-justify-center">
        {t('subHeader')}
      </Text>

      <Form
        layout="vertical"
        className="tw-w-full"
        size="small"
        onFinish={handleStartTrial}
        initialValues={{
          selectedCountry: 'finland',
          phoneNumber: CountryCodeMapping.finland,
        }}
      >
        <InputWithFormItem
          type="password"
          label={t('passwordLabel')}
          inputName="password"
          size="middle"
          PrefixIcon={MdOutlinePassword}
          rules={[
            {
              required: true,
              message: t('passwordIsRequiredRule'),
            },
            {
              pattern: new RegExp(SIGN_UP_PASSWORD_PATTERN),
              message: t('passwordLengthRule'),
            },
          ]}
        />

        <InputPhone
          label={t('phoneNumberLabel')}
          inputName="phoneNumber"
          size="middle"
          placeholderText={t('phoneNumber')}
          rules={[
            {
              required: true,
              message: t('phoneNumberIsRequiredRule'),
            },
            { min: 7, message: t('phoneNumberError') },
            { max: 14, message: t('phoneNumberError') },
          ]}
        />

        <InputWithFormItem
          label={t('organizationNameLabel')}
          inputName="companyName"
          size="middle"
          PrefixIcon={MdBusiness}
        />

        <InputWithFormItem
          label={t('fullNameLabel')}
          inputName="userFullName"
          size="middle"
          PrefixIcon={MdOutlineAccountCircle}
          rules={[
            {
              required: true,
              message: t('fullNameIsRequiredRule'),
            },
          ]}
        />

        <Button
          submit
          size="middle"
          type="primary"
          label={t('startBtn')}
          fullWidth
          loading={isLoading}
        />
      </Form>

      <ContactUs wrapperClass="tw-flex tw-justify-center" />
    </Space>
  );

  return (
    <CenterPanel>
      <div className="tw-w-[450px]">
        {error && <AlertMessage className="tw-my-2" message={error} showIcon />}

        <SurfaceContainer maxWidth={530} noBorder>
          {tokenError ? renderInvalidToken() : renderProfileForm()}
        </SurfaceContainer>
      </div>
    </CenterPanel>
  );
};

export default connect(null, {
  loginUserAction: loginUser,
  logoutUserAction: logoutUser,
})(CompleteSignUp);
